import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DeleteOutline as TrashIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CalendarToday,
  Person,
  Visibility as VisibilityIcon,
  Upload as UploadIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
  MedicalInformation as MedicalInformationIcon,
  PictureAsPdfTwoTone,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { bookingsService } from './bookings.service';
import { Booking } from './bookings.dto';
import { useAuth } from '../../contexts/AuthContext';
import {
  DEFAULT_CURRENCY,
  PAYMENT_STATUS,
  REPORT_STATUS,
  BOOKING_STATUS,
  PAYMENT_METHOD,
} from '@/constants';
import { formatDateOnly, formatTime } from '../../helpers';
import { useToast } from '../../contexts/ToastContext';
import moment from 'moment-timezone';
const Bookings = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, loading: authLoading, logout } = useAuth();
  const { showSuccess, showError, showInfo } = useToast();

  // Search/filter/page state is seeded from the URL (and kept in sync with
  // it below) rather than always starting from empty defaults. Without
  // this, opening a booking's detail view unmounts this page entirely, and
  // returning to it (browser back, or the detail page's own back button)
  // remounted it from scratch - silently discarding whatever date filter,
  // search term, or page the admin had set, back to the unfiltered list.
  // Reading/writing the URL means that state survives the round trip
  // (and, as a bonus, filtered views become bookmarkable/shareable).
  const [searchParams, setSearchParams] = useSearchParams();

  // State management
  const [searchTerm, setSearchTerm] = useState(
    () => searchParams.get('search') || ''
  );
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(
    () => searchParams.get('search') || ''
  );
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(
    () => Number(searchParams.get('page')) || 1
  );
  const [totalPages, setTotalPages] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(
    null
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [emailCustomer, setEmailCustomer] = useState(true);
  const [startDate, setStartDate] = useState(
    () => searchParams.get('startDate') || ''
  );
  const [endDate, setEndDate] = useState(
    () => searchParams.get('endDate') || ''
  );
  const [tempStartDate, setTempStartDate] = useState(
    () => searchParams.get('startDate') || ''
  );
  const [tempEndDate, setTempEndDate] = useState(
    () => searchParams.get('endDate') || ''
  );
  const [exporting, setExporting] = useState(false);
  // Requisition PDF generation renders a real document server-side
  // (Puppeteer) and can take a few seconds - with no feedback the button
  // looked inert, so admins would click it repeatedly. Tracks booking ids
  // currently generating (a Set, not a single boolean, since more than one
  // row's PDF could be requested in quick succession).
  const [generatingRequisitionIds, setGeneratingRequisitionIds] = useState<
    Set<number>
  >(new Set());
  const itemsPerPage = 10;

  // Keep the URL's query string in sync with the current filter/search/page
  // state, so it's there to restore from on the way back from a booking's
  // detail view. `replace` (not push) so tweaking a filter doesn't spam
  // browser history - only navigating to a *different route* (e.g. opening
  // a booking) pushes a new entry, which is what "back" should return past.
  useEffect(() => {
    const params: Record<string, string> = {};
    if (debouncedSearchTerm) params.search = debouncedSearchTerm;
    if (currentPage > 1) params.page = String(currentPage);
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    setSearchParams(params, { replace: true });
  }, [debouncedSearchTerm, currentPage, startDate, endDate, setSearchParams]);

  // Set up logout callback for session expired
  useEffect(() => {
    bookingsService.setLogoutCallback(logout);
  }, [logout]);

  // Debounce search term
  useEffect(() => {
    console.log('🔍 Search term changed to:', searchTerm);
    const timer = setTimeout(() => {
      console.log(
        '⏱️ Debounce complete - setting debouncedSearchTerm to:',
        searchTerm
      );
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      console.log('🧹 Clearing debounce timeout');
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Reset page when search term changes - but not on the initial mount,
  // where debouncedSearchTerm is only just being seeded from the URL and a
  // page restored from that same URL (e.g. returning from a booking's
  // detail view) must not be clobbered back to 1.
  const isInitialSearchRender = useRef(true);
  useEffect(() => {
    if (isInitialSearchRender.current) {
      isInitialSearchRender.current = false;
      return;
    }
    console.log('📄 Debounced search term changed, resetting page to 1');
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Fetch bookings from API
  const fetchBookings = useCallback(async () => {
    if (!token || !isAuthenticated) {
      setLoading(false);
      return;
    }

    console.log(
      '🔄 Fetching bookings - Page:',
      currentPage,
      'Search:',
      debouncedSearchTerm,
      'Start Date:',
      startDate,
      'End Date:',
      endDate
    );

    try {
      setLoading(true);
      setError(null);

      const response = await bookingsService.getBookings(token, {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchTerm || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      setBookings(response.data || []);
      setTotalPages(response.meta?.lastPage || 1);
      setTotalBookings(response.meta?.total || 0);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [
    token,
    isAuthenticated,
    currentPage,
    debouncedSearchTerm,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Handlers
  const handleDeleteClick = (id: number) => {
    setBookingToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!token || !bookingToDelete) return;

    try {
      setDeleting(true);
      await bookingsService.deleteBooking(token, bookingToDelete);
      showSuccess('Booking deleted successfully');
      setDeleteDialogOpen(false);
      setBookingToDelete(null);

      // If current page becomes empty after delete, go to previous page
      if (bookings.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchBookings();
      }
    } catch (err) {
      console.error('Error deleting booking:', err);
      showError(
        err instanceof Error ? err.message : 'Failed to delete booking'
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBookingToDelete(null);
  };

  const handleUploadClick = (bookingId: number) => {
    setSelectedBookingId(bookingId);
    setUploadDialogOpen(true);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type === 'application/pdf') {
        setSelectedFile(file);
      } else {
        showError('Please select a PDF file');
      }
    }
  };

  const handleUploadConfirm = async () => {
    if (!token || !selectedBookingId || !selectedFile) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('emailCustomer', String(emailCustomer));

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      await bookingsService.uploadReport(token, selectedBookingId, formData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      showSuccess('Report uploaded successfully');
      setUploadDialogOpen(false);
      setSelectedBookingId(null);
      setSelectedFile(null);
      setUploadProgress(0);

      // Refresh bookings to update report status
      fetchBookings();
    } catch (err) {
      console.error('Error uploading report:', err);
      showError(err instanceof Error ? err.message : 'Failed to upload report');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleUploadCancel = () => {
    setUploadDialogOpen(false);
    setSelectedBookingId(null);
    setSelectedFile(null);
    setEmailCustomer(true);
    setUploadProgress(0);
  };

  const handleEdit = (id: number) => {
    navigate(`/bookings/edit/${id}`);
  };

  const handleView = (id: number) => {
    navigate(`/bookings/view/${id}`);
  };

  const handleViewPdf = async (bookingId: number) => {
    if (!token) return;

    try {
      const blob = await bookingsService.downloadReport(token, bookingId);

      // Create a URL for the blob
      const url = URL.createObjectURL(blob);

      // Open PDF in new tab
      window.open(url, '_blank');

      // Clean up the URL after a delay to free memory
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);

      showSuccess('PDF report opened successfully');
    } catch (err) {
      console.error('Error viewing PDF:', err);
      showError(
        err instanceof Error ? err.message : 'Failed to view PDF report'
      );
    }
  };

  const handleGenerateRequisition = async (booking: Booking) => {
    if (!token) return;
    if (generatingRequisitionIds.has(booking.id)) return;

    setGeneratingRequisitionIds(prev => new Set(prev).add(booking.id));
    showInfo('Generating requisition PDF, please wait...');

    try {
      const blob = await bookingsService.downloadRequisitionPdf(
        token,
        booking.id
      );

      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
      showSuccess('Requisition PDF ready - opened in a new tab');
    } catch (err) {
      console.error('Error generating requisition PDF:', err);

      // The backend's "No requisition configured..." message references
      // the Centre/Service by raw id - show the admin the actual names
      // instead, since we already have them on this booking row.
      const centreName = booking.diagnosticCenterName || 'this centre';
      const serviceName = booking.title || 'this service';
      const rawMessage =
        err instanceof Error ? err.message : 'Failed to generate requisition';
      const message = /^No requisition configured for/i.test(rawMessage)
        ? `No requisition configured for "${serviceName}" at "${centreName}". Please configure it first.`
        : rawMessage;

      showError(message);
    } finally {
      setGeneratingRequisitionIds(prev => {
        const next = new Set(prev);
        next.delete(booking.id);
        return next;
      });
    }
  };

  const handleAddBooking = () => {
    navigate('/bookings/add');
  };

  const handleExportData = async () => {
    if (!token) return;

    try {
      setExporting(true);

      // Fetch all bookings without pagination for export
      const response = await bookingsService.getBookings(token, {
        limit: 100000, // Get all bookings
        exportData: true,
        search: debouncedSearchTerm || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      const dataToExport = response.data;

      // Convert to CSV with all fields
      const headers = [
        'ID',
        'Booking Ref',
        'First Name',
        'Last Name',
        'DOB',
        'Gender',
        'Email',
        'Phone',
        'Diagnostic Center Phone',
        'Diagnostic Center Email',
        'Diagnostic Center Address',
        'Diagnostic Center City',
        'Diagnostic Center Postal Code',
        'Service Title',
        'Service Duration',
        'Booking Date',
        'Booking Time',
        'Amount',
        'Status',
        'Payment Status',
        'Payment Method',
        'Report Status',
        'Report URL',
        'Created At',
      ];

      const csvRows = [
        headers.join(','),
        ...dataToExport.map(booking => {
          // Format questionnaires as a readable string
          // const questionnaires =
          //   booking.questionnaires
          //     ?.map(q => `${q.question}: ${q.answer}`)
          //     .join('; ') || '';

          return [
            booking.id,
            booking.bookingRef,
            booking.firstName,
            booking.lastName,
            booking.dob,
            booking.gender || '',
            booking.email,
            booking.phone,
            booking.diagnosticCenterPhone || '',
            booking.diagnosticCenterEmail || '',
            `"${(booking.diagnosticCenterAddress || '').replace(/"/g, '""')}"`,
            booking.diagnosticCenterCity || '',
            booking.diagnosticCenterPostalCode || '',
            `"${(booking.title || '').replace(/"/g, '""')}"`,
            booking.duration || '',
            // `"${(booking.description || '').replace(/"/g, '""')}"`,
            booking.bookingDate,
            booking.bookingTime,
            DEFAULT_CURRENCY + booking.amount,
            BOOKING_STATUS(booking.status).label,
            PAYMENT_STATUS(booking.paymentStatus).label,
            PAYMENT_METHOD(booking.paymentMethod).label || '',
            REPORT_STATUS(booking.reportStatus).label,
            booking.reportUrl || '',
            // `"${(booking.medicalInformation || '').replace(/"/g, '""')}"`,
            new Date(booking.createdAt).toLocaleDateString(),
            // `"${questionnaires.replace(/"/g, '""')}"`,
          ].join(',');
        }),
      ];

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with date range info
      let filename = 'bookings';
      if (startDate && endDate) {
        filename += `_${startDate}_to_${endDate}`;
      } else if (startDate) {
        filename += `_from_${startDate}`;
      } else if (endDate) {
        filename += `_until_${endDate}`;
      } else {
        filename += `_${new Date().toISOString().split('T')[0]}`;
      }
      filename += '.csv';

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const exportMessage =
        startDate || endDate
          ? `Exported ${dataToExport.length} bookings for selected date range`
          : `Exported ${dataToExport.length} bookings`;
      showSuccess(exportMessage);
    } catch (err) {
      console.error('Error exporting bookings:', err);
      showError(
        err instanceof Error ? err.message : 'Failed to export bookings'
      );
    } finally {
      setExporting(false);
    }
  };

  const handleApplyFilters = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setTempStartDate('');
    setTempEndDate('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    console.log(
      '📄 Pagination clicked - Changing page from',
      currentPage,
      'to',
      page
    );
    setCurrentPage(page);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
  };

  // Loading state
  if (authLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Render
  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h4">Bookings Management</Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            color="warning"
            startIcon={<TrashIcon />}
            onClick={() => navigate('/bookings/trash')}
            disabled={!isAuthenticated}
          >
            Trash
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddBooking}
            disabled={!isAuthenticated}
          >
            Add Booking
          </Button>
        </Box>
      </Box>

      {/* Filters and Search Bar */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
          <TextField
            type="date"
            label="Start Date"
            value={tempStartDate}
            onChange={e => setTempStartDate(e.target.value)}
            sx={{ minWidth: 180 }}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            type="date"
            label="End Date"
            value={tempEndDate}
            onChange={e => setTempEndDate(e.target.value)}
            sx={{ minWidth: 180 }}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: tempStartDate || undefined,
            }}
          />

          <Button
            variant="contained"
            startIcon={<FilterListIcon />}
            onClick={handleApplyFilters}
            disabled={!tempStartDate && !tempEndDate}
          >
            Apply Filter
          </Button>

          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            disabled={
              !searchTerm &&
              !startDate &&
              !endDate &&
              !tempStartDate &&
              !tempEndDate
            }
          >
            Clear All
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="contained"
            startIcon={
              exporting ? <CircularProgress size={20} /> : <FileDownloadIcon />
            }
            onClick={handleExportData}
            disabled={loading || exporting || bookings.length === 0}
            color="success"
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </Box>

        <TextField
          fullWidth
          placeholder="Search bookings by booking ref, name, email, phone..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            ),
            endAdornment: (
              <Box display="flex" alignItems="center" gap={1}>
                {searchTerm !== debouncedSearchTerm && (
                  <CircularProgress size={20} />
                )}
                {searchTerm && (
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    disabled={loading}
                    aria-label="Clear search"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ),
          }}
        />
      </Box>

      {/* Stats */}
      {isAuthenticated && !loading && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Total Bookings: {totalBookings}
          </Typography>
        </Box>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Bookings Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Booking Ref</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Date & Time</TableCell>
                {/* <TableCell>Service</TableCell> */}
                <TableCell>Amount</TableCell>
                <TableCell>Payment Status</TableCell>
                <TableCell>Report Status</TableCell>
                <TableCell>Report Password</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Loading bookings...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : bookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm
                        ? 'No bookings found matching your search'
                        : 'No bookings found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                bookings.map((booking, index) => (
                  <TableRow key={booking.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        #{booking.bookingRef}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: 'primary.main',
                          }}
                        >
                          <Person fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {booking.firstName} {booking.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            DOB: {formatDateOnly(booking.dob)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{booking.email}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {booking.phone}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarToday fontSize="small" color="action" />
                        <Box>
                          <Typography variant="body2">
                            {formatDateOnly(booking.bookingDate)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatTime(booking.bookingTime)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    {/* <TableCell>
                      <Typography variant="body2">
                        {booking.title || `Service #${booking.serviceId}`}
                      </Typography>
                    </TableCell> */}
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {DEFAULT_CURRENCY}
                        {booking.amount}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={PAYMENT_STATUS(booking.paymentStatus).label}
                        color={
                          PAYMENT_STATUS(booking.paymentStatus).color as any
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={REPORT_STATUS(booking.reportStatus)?.label}
                        color={
                          REPORT_STATUS(booking.reportStatus)?.color as any
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {(
                          (booking?.firstName + booking?.lastName)
                            ?.replace(/\s+/g, '')
                            .substring(0, 4) +
                          moment(booking?.dob).format('DDMM')
                        ).toUpperCase()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={BOOKING_STATUS(booking.status)?.label}
                        color={BOOKING_STATUS(booking.status)?.color as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center">
                        <IconButton
                          color="info"
                          onClick={() => handleView(booking.id)}
                          size="small"
                          disabled={loading}
                          title="View Booking"
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          color="primary"
                          onClick={() => handleEdit(booking.id)}
                          size="small"
                          disabled={loading}
                          title="Edit Booking"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="primary"
                          onClick={() => handleGenerateRequisition(booking)}
                          size="small"
                          disabled={
                            loading || generatingRequisitionIds.has(booking.id)
                          }
                          title={
                            generatingRequisitionIds.has(booking.id)
                              ? 'Generating requisition PDF...'
                              : 'View Requisition PDF'
                          }
                        >
                          {generatingRequisitionIds.has(booking.id) ? (
                            <CircularProgress size={20} />
                          ) : (
                            <PictureAsPdfTwoTone />
                          )}
                        </IconButton>
                        {booking.paymentStatus === 1 && (
                          <IconButton
                            color="secondary"
                            onClick={() => handleUploadClick(booking.id)}
                            size="small"
                            disabled={loading || uploading}
                            title="Upload Report"
                          >
                            <UploadIcon />
                          </IconButton>
                        )}
                        {booking.reportStatus === 1 && (
                          <IconButton
                            color="success"
                            onClick={() => handleViewPdf(booking.id)}
                            size="small"
                            disabled={loading}
                            title="View Medical Report PDF"
                          >
                            <MedicalInformationIcon />
                          </IconButton>
                        )}
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(booking.id)}
                          size="small"
                          disabled={loading || deleting}
                          title="Delete Booking"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      {!loading && totalPages > 1 ? (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" mt={3}>
          <Typography variant="caption" color="text.secondary">
            {loading
              ? 'Loading...'
              : `Showing page ${currentPage} of ${totalPages}`}
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={deleting ? undefined : handleDeleteCancel}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this booking? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Report Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleUploadCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Report</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} sx={{ mt: 2 }}>
            {/* File Selection */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Select PDF File
              </Typography>
              <input
                accept=".pdf"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                onChange={handleFileSelect}
                disabled={uploading}
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  disabled={uploading}
                  startIcon={<UploadIcon />}
                >
                  Choose PDF File
                </Button>
              </label>
              {selectedFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {selectedFile.name}
                </Typography>
              )}
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={emailCustomer}
                      onChange={e => setEmailCustomer(e.target.checked)}
                      disabled={uploading}
                      color="primary"
                    />
                  }
                  label="Email medical report to customer"
                />
              </Box>
            </Box>
            {/* Upload Progress */}
            {uploading && (
              <Box>
                <Typography variant="body2" gutterBottom>
                  Uploading... {uploadProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadCancel} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUploadConfirm}
            variant="contained"
            disabled={!selectedFile || uploading}
            startIcon={
              uploading ? <CircularProgress size={20} /> : <UploadIcon />
            }
          >
            {uploading ? 'Uploading...' : 'Upload Report'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Bookings;
